import {
  Badge,
  Button,
  Col,
  Descriptions,
  Divider,
  List,
  Modal,
  Popconfirm,
  Typography,
  Input,
  message,
} from "antd";
import React, { useEffect, useState } from "react";
import { IBusinessFull } from "../../../../../interfaces/businessFull.interface";
import { IOrganization } from "../../../../../interfaces/organization.interface";
import {
  UserOutlined,
  FileOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import Loader from "../../../../common/loader/Loader";
import {
  deleteAndArchiveBusinessThunk,
  revertSubmissionBusinessThunk,
  editBusinessThunk,
  getBusinessThunk,
  selectCurrentBusiness,
  deactivateBusinessThunk,
} from "../../../businessesSlice";
import {
  getOrganizationForBusinessServer,
  removeBusinessFromOrganizationServer,
} from "../../../businessesApi";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { downloadFile } from "../../../../../utils/downloadFile";
const { Paragraph } = Typography;

interface IPropsBusinessSensitiveActions {
  business: Partial<IBusinessFull>;
  setVisible: (boolean: boolean) => void;
}

export default function BusinessSensitiveActions({
  business,
  setVisible,
}: IPropsBusinessSensitiveActions) {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [deactivationReason, setDeactivationReason] = useState("");
  const [organization, setOrganization] = useState<IOrganization | null>(null);

  useEffect(() => {
    const fetchOrganization = async () => {
      if (!business?._id) return;
      try {
        console.log("fetching organization for business", business._id);
        const org = await getOrganizationForBusinessServer(business._id);
        setOrganization((org as any) || null);
      } catch (error) {
        console.log("Failed to fetch organization for business", error);
        setOrganization(null);
      }
    };
    fetchOrganization();
  }, [business?._id]);

  const handleDeleteAndArchive = async () => {
    if (!business?._id) return;

    try {
      setIsLoading(true);
      await dispatch(deleteAndArchiveBusinessThunk({ _id: business._id }));
      setSuccessMessage("Business deleted and archived successfully");
      setTimeout(() => {
        setSuccessMessage("");
        setVisible(false);
      }, 2000);
    } catch (error) {
      console.log("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivateBusiness = async (reason: string) => {
    if (!business?._id || !reason) return;
    try {
      setIsLoading(true);
      await dispatch(deactivateBusinessThunk({ _id: business._id, reason }));
      setSuccessMessage("Business request to deactivate sent successfully");
      setTimeout(() => {
        setSuccessMessage("");
        setVisible(false);
      }, 2000);
    } catch (error) {
      console.log("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevertSubmission = async () => {
    if (!business?._id) return;
    try {
      setIsLoading(true);
      await dispatch(revertSubmissionBusinessThunk({ _id: business._id }));
      setSuccessMessage("Business reverted to pending submission successfully");
      setTimeout(() => {
        setSuccessMessage("");
        setVisible(false);
      }, 2000);
    } catch (error) {
      console.log("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemovePartner = async () => {
    if (!business?._id) return;
    try {
      setIsLoading(true);
      await dispatch(
        editBusinessThunk({
          _id: business._id,
          updatedFields: {
            partnerId: "",
            partnerPurchPriceProfileId: "",
          },
        })
      ).unwrap();
      setSuccessMessage("Partner removed from business successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
      try {
        await dispatch(getBusinessThunk(business._id)).unwrap();
      } catch {
        message.warning(
          "Partner removed, but refreshing the merchant failed. Reopen the editor to see the change."
        );
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          error?.response?.data?.code ||
          error?.message ||
          error?.code ||
          "Failed to remove partner from business"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFromOrganization = async () => {
    if (!business?._id || !organization?._id) return;
    try {
      setIsLoading(true);
      await removeBusinessFromOrganizationServer({
        organizationId: organization._id,
        businessId: business._id,
      });
      setSuccessMessage("Business removed from organization successfully");
      setTimeout(() => {
        setSuccessMessage("");
        setVisible(false);
      }, 2000);
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          error?.response?.data?.code ||
          "Failed to remove business from organization"
      );
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div style={{ position: "relative", minHeight: "100px" }}>
      {!isLoading && (
        <>
          {business?.status !== "deactivated" &&
            !business?.firstTsxDate &&
            !successMessage && (
              <>
                <Divider />

                <Typography.Title level={5}>
                  Delete Unused Business
                </Typography.Title>
                <Typography.Text
                  style={{ display: "block", marginBottom: "10px" }}
                >
                  Will delete business without any transactions and archive it
                  in the business graveyard for 30 days.
                </Typography.Text>

                <Popconfirm
                  title="Delete this business?"
                  onConfirm={handleDeleteAndArchive}
                  okButtonProps={{ danger: true }}
                  children={
                    <Button
                      type="primary"
                      danger
                      style={{ marginBottom: "40px" }}
                    >
                      Delete
                    </Button>
                  }
                ></Popconfirm>
              </>
            )}
          {business?.status !== "deactivated" && !successMessage && (
            <>
              <Divider />
              <Typography.Title level={5}>
                Request to deactivate the business
              </Typography.Title>

              <Typography.Text
                style={{ display: "block", marginBottom: "6px" }}
              >
                Reason for deactivation
              </Typography.Text>
              <Input.TextArea
                value={deactivationReason}
                onChange={(e) => setDeactivationReason(e.target.value)}
                placeholder="Why is the business leaving us?"
                rows={3}
                style={{ marginBottom: "10px" }}
              />
              <Popconfirm
                title="Deactivate this business?"
                onConfirm={() =>
                  handleDeactivateBusiness(deactivationReason.trim())
                }
                okButtonProps={{ danger: true }}
              >
                <Button
                  type="primary"
                  danger
                  style={{ marginBottom: "10px" }}
                  disabled={!deactivationReason.trim()}
                >
                  Request Deactivation
                </Button>
              </Popconfirm>
            </>
          )}
          {business?.status === "pendingVerification" && !successMessage && (
            <>
              <Divider />

              <Typography.Title level={5}>
                Revert to Pending Submission
              </Typography.Title>
              <Typography.Text
                style={{ display: "block", marginBottom: "10px" }}
              >
                Will revert the business to pending submission status. This will
                allow the business to be edited and resubmitted.
              </Typography.Text>
              <Popconfirm
                title="Revert to pending submission?"
                onConfirm={handleRevertSubmission}
                okButtonProps={{ danger: true }}
              >
                <Button type="primary" style={{ marginBottom: "10px" }}>
                  Revert
                </Button>
              </Popconfirm>
            </>
          )}
          {business?.partnerId && !successMessage && (
            <>
              <Divider />

              <Typography.Title level={5}>Remove Partner</Typography.Title>
              <Typography.Text
                style={{ display: "block", marginBottom: "10px" }}
              >
                Will remove the partner{", "}
                <span style={{ fontStyle: "italic" }}>
                  {
                    //@ts-ignore
                    business?.partnerId?.name
                  }
                  {", "}
                </span>
                association from this merchant.
              </Typography.Text>
              <Popconfirm
                title="Remove partner from this business?"
                onConfirm={handleRemovePartner}
                okButtonProps={{ danger: true }}
              >
                <Button type="primary" danger style={{ marginBottom: "10px" }}>
                  Remove Partner
                </Button>
              </Popconfirm>
            </>
          )}
          {organization?._id && !successMessage && (
            <>
              <Divider />
              <Typography.Title level={5}>
                Remove from Organization: {organization?.name}
              </Typography.Title>
              <Typography.Text
                style={{ display: "block", marginBottom: "10px" }}
              >
                Will remove this business from the organization{" "}
                <span style={{ fontStyle: "italic" }}>
                  {organization?.name || organization?._id}
                </span>
                .
              </Typography.Text>
              <Popconfirm
                title="Remove business from this organization?"
                onConfirm={handleRemoveFromOrganization}
                okButtonProps={{ danger: true }}
              >
                <Button type="primary" danger style={{ marginBottom: "10px" }}>
                  Remove from Organization
                </Button>
              </Popconfirm>
            </>
          )}
        </>
      )}

      {/* {!(
        [
          "pendingSubmission",
          "deactivated",
          "pendingVerification",
          "verified",
        ].includes(business?.status || "") &&
        !business?.partnerId &&
        !organization
      ) && (
        <div style={{ marginTop: "30px" }}>
          No actions available for this business
        </div>
      )} */}

      {successMessage?.length > 0 && (
        <Typography.Text
          style={{
            display: "block",
            marginBottom: "10px",
            marginTop: "30px",
            color: "green",
            fontSize: "16px",
          }}
        >
          <CheckCircleOutlined style={{ color: "green", marginRight: "3px" }} />
          {successMessage}
        </Typography.Text>
      )}

      {isLoading && <Loader text="Please wait" />}
    </div>
  );
}
